/**
 * RiskInsightPanel — Vertical stack of 360 assessment insight cards
 * Fetches Iris-generated findings from backend, falls back to static content
 */
import React, { useEffect, useState } from 'react';
import { Briefcase, Code, Database, ShieldCheck, UserCheck } from 'lucide-react';
import { apiConfig } from '../../../config/apiConfig';
import { authHeaders, getAuthToken } from '../../../services/authHeaders';
import RiskInsightCard from './RiskInsightCard';

// Static fallback findings (shown while Iris is loading or unavailable)
const FALLBACK_FINDINGS = {
  'E360-ID': {
    findings: [
      'Governance framework assessment indicates areas for improvement in board-level cybersecurity oversight.',
      'Policy coverage and institutional knowledge distribution should be evaluated for resilience gaps.',
      'Outside pressure factors may be creating additional risk exposure for the organization.',
    ],
    topAction: 'Conduct a governance framework review to identify oversight gaps',
    riskLevel: null,
  },
  'E360-TD': {
    findings: [
      'Technical infrastructure shows indicators of accumulated technical debt requiring attention.',
      'Architecture documentation and system inventory completeness should be assessed.',
      'Patch management and system modernization cadence may need acceleration.',
    ],
    topAction: 'Complete a legacy system inventory and prioritize critical upgrades',
    riskLevel: null,
  },
  D360: {
    findings: [
      'Data management practices show room for improvement in classification and handling procedures.',
      'Backup and recovery capabilities should be validated against current business requirements.',
      'Third-party data sharing agreements may benefit from enhanced security provisions.',
    ],
    topAction: 'Implement formal data classification and protection procedures',
    riskLevel: null,
  },
  C360: {
    findings: [
      'Cyber defense capabilities indicate coverage gaps that may expose the organization to threats.',
      'Security operations maturity should be evaluated for proactive threat detection capability.',
      'Network segmentation and endpoint coverage merit review for comprehensive protection.',
    ],
    topAction: 'Assess and extend security monitoring coverage across all endpoints',
    riskLevel: null,
  },
  P360: {
    findings: [
      'Security awareness program effectiveness shows areas for strengthening.',
      'Cybersecurity staffing levels should be benchmarked against peer organizations.',
      'Access management review frequency may need to increase for privileged accounts.',
    ],
    topAction: 'Benchmark security team capacity and implement regular access reviews',
    riskLevel: null,
  },
};

const DEEP_DIVE_LABELS = {
  'E360-ID': 'Deep Dive — Enterprise Governance',
  'E360-TD': 'Deep Dive — Technical Debt',
  D360: 'Deep Dive — Data Security',
  C360: 'Deep Dive — Cyber Defenses',
  P360: 'Deep Dive — People & Culture',
};

// Cards where a PRISM technical assessment CTA makes sense
const PRISM_CTA_CARDS = new Set(['C360', 'E360-TD']);

export default function RiskInsightPanel({ analytics360, sessionId }) {
  const [irisFindings, setIrisFindings] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    setLoading(true);

    fetch(apiConfig.foundations.get360Findings(sessionId), { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        if (!cancelled && data.success && data.findings) {
          setIrisFindings(data.findings);
        }
      })
      .catch(() => {
        // Fallback silently — static findings will show
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [sessionId]);

  const [prismLinked, setPrismLinked] = useState(null); // null = unknown, { linked, prismBaseUrl, prismProjectId }

  // Check if company already has a PRISM link (skip bridge auth on return visits)
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;

    // Decode company_id from JWT (set by Foundations auth.js)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.company_id) return;

      fetch(`${apiConfig.apiBaseUrl}/foundations/company/${payload.company_id}/prism-link`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(data => setPrismLinked(data))
        .catch(() => setPrismLinked({ linked: false }));
    } catch {
      // Token decode failed — not fatal
    }
  }, []);

  async function handlePrismClick() {
    try {
      // If already linked, navigate directly (skip bridge auth)
      if (prismLinked?.linked && prismLinked.prismBaseUrl && prismLinked.prismProjectId) {
        window.open(
          `${prismLinked.prismBaseUrl}/projects-v2/${prismLinked.prismProjectId}?from=foundations`,
          'prism'
        );
        return;
      }

      // First time: use bridge auth flow
      const response = await fetch(`${apiConfig.foundations.getDeepDive('PRISM')}?sessionId=${sessionId}`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.success && data.redirectUrl) {
        window.open(data.redirectUrl, 'prism');
      } else {
        console.warn('PRISM redirect unavailable:', data.error || 'Unknown error');
      }
    } catch (err) {
      console.error('PRISM redirect error:', err);
    }
  }

  if (!analytics360?.data) return null;

  const { enterprise360, data360, cyber360, people360 } = analytics360.data;

  const cards = [
    enterprise360?.components?.institutionalDebt && {
      key: 'E360-ID',
      title: 'Enterprise360™',
      subtitle: 'Institutional Debt',
      dartboardTitle: 'E360-ID',
      score: enterprise360.components.institutionalDebt.totalScore,
      componentData: enterprise360.components.institutionalDebt,
      icon: Briefcase,
      iconColor: '#3b82f6',
    },
    enterprise360?.components?.technicalDebt && {
      key: 'E360-TD',
      title: 'Enterprise360™',
      subtitle: 'Technical Debt',
      dartboardTitle: 'E360-TD',
      score: enterprise360.components.technicalDebt.totalScore,
      componentData: enterprise360.components.technicalDebt,
      icon: Code,
      iconColor: '#3b82f6',
    },
    data360 && {
      key: 'D360',
      title: 'Data360™',
      subtitle: null,
      dartboardTitle: 'D360',
      score: data360.totalScore,
      componentData: data360,
      icon: Database,
      iconColor: '#db2777',
    },
    cyber360 && {
      key: 'C360',
      title: 'Cyber360™',
      subtitle: null,
      dartboardTitle: 'C360',
      score: cyber360.totalScore,
      componentData: cyber360,
      icon: ShieldCheck,
      iconColor: '#dc2626',
    },
    people360 && {
      key: 'P360',
      title: 'People360™',
      subtitle: null,
      dartboardTitle: 'P360',
      score: people360.totalScore,
      componentData: people360,
      icon: UserCheck,
      iconColor: '#ca8a04',
    },
  ].filter(Boolean);

  function getFindingsForCard(cardKey, score) {
    // Use Iris findings if available, but never its riskLevel —
    // posture badge is derived from sector colors by RiskInsightCard.
    const iris = irisFindings?.[cardKey];
    if (iris) return { ...iris, riskLevel: null };

    const fb = FALLBACK_FINDINGS[cardKey];
    if (!fb) return { findings: [], topAction: null, riskLevel: null };

    // Let RiskInsightCard derive posture from sector colors
    return { ...fb, riskLevel: fb.riskLevel || null };
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          fontSize: '0.75rem', color: 'var(--ra-text-secondary)',
          fontFamily: "'Source Sans 3', sans-serif",
        }}>
          Generating insights…
        </div>
      )}
      {cards.map((card) => {
        const { findings, topAction, riskLevel } = getFindingsForCard(card.key, card.score);
        const showPrism = PRISM_CTA_CARDS.has(card.key);
        return (
          <RiskInsightCard
            key={card.key}
            title={card.title}
            subtitle={card.subtitle}
            dartboardTitle={card.dartboardTitle}
            score={card.score}
            riskLevelOverride={riskLevel}
            componentData={card.componentData}
            findings={findings}
            topAction={topAction}
            deepDiveLabel={DEEP_DIVE_LABELS[card.key]}
            deepDiveDisabled
            deepDiveTooltip="Cyber360 deep-dive assessments — coming Q2 2026"
            prismLabel={showPrism ? (prismLinked?.linked ? 'Open in PRISM' : 'Launch in PRISM') : undefined}
            onPrismClick={showPrism ? handlePrismClick : undefined}
            icon={card.icon}
            iconColor={card.iconColor}
          />
        );
      })}
    </div>
  );
}
